import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { unitConversion } from '@/pages/dashboard/utils/utils';
import { getCardChart, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import { yAxisFormat } from '@/pages/dashboard/components/Chart/_config/utils';
import type { ILiveMetricTrendData } from './types';

export const getMonthLabel = (value: any): string => {
  const rawValue = String(value || '');
  const monthMatch = rawValue.match(/^\d{4}[-/](\d{1,2})(?:[-/]\d{1,2})?$/);
  if (monthMatch?.[1]) {
    return monthMatch[1].padStart(2, '0');
  }
  return rawValue.slice(-2);
};

export const getYearMonthLabel = (value: any): string => {
  const rawValue = String(value || '');
  const yearMonthMatch = rawValue.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    return `${year}-${month.padStart(2, '0')}`;
  }
  return rawValue;
};

const isValidTrendPoint = (v: any): boolean => {
  if (v === null || v === undefined) return false;
  const raw = typeof v === 'object' ? v?.value : v;
  if (raw === null || raw === undefined) return false;
  return !Number.isNaN(Number(raw));
};

// Single-point series cannot draw a line; force symbol visibility.
export const normalizeTrendSeries = (originSeries: any[] | undefined): any[] => {
  if (!Array.isArray(originSeries)) return [];

  return originSeries.map((item: any) => {
    const data = item?.data;
    if (!Array.isArray(data)) return item;

    const validIndexes: number[] = [];
    data.forEach((point: any, idx: number) => {
      if (isValidTrendPoint(point)) validIndexes.push(idx);
    });
    if (validIndexes.length !== 1) return item;

    const onlyIdx = validIndexes[0];
    const originalPoint = data[onlyIdx];
    const isObjectPoint = originalPoint && typeof originalPoint === 'object';
    const pointItemStyle = isObjectPoint ? originalPoint?.itemStyle || {} : {};
    const normalizedPoint = {
      ...(isObjectPoint ? originalPoint : { value: originalPoint }),
      itemStyle: {
        ...pointItemStyle,
        opacity: 1,
      },
      symbol: 'circle',
      symbolSize: 6,
    };
    const newData = data.slice();
    newData[onlyIdx] = normalizedPoint;

    return {
      ...item,
      showSymbol: true,
      showAllSymbol: true,
      data: newData,
    };
  });
};

export const hasRenderableTrendData = (series: any[] | undefined): boolean => {
  if (!Array.isArray(series) || series.length === 0) {
    return false;
  }
  return series.some((item: any) => {
    const data = item?.data;
    if (!Array.isArray(data) || data.length === 0) {
      return false;
    }
    return data.some((point: any) => isValidTrendPoint(point));
  });
};

export const shouldRenderTrendChart = (
  trendData?: ILiveMetricTrendData,
): boolean => {
  return Boolean(
    trendData &&
    (trendData?.xAxisData?.length ?? 0) > 0 &&
    (trendData?.series?.length ?? 0) > 0 &&
    hasRenderableTrendData(trendData?.series),
  );
};

interface ITooltipFormatterOptions {
  title: string;
  formatter?: string;
  unit?: string;
}

export const buildTrendTooltipFormatter = ({
  title,
  formatter,
  unit,
}: ITooltipFormatterOptions) => (param: string | any[]): string => {
  const paramsArr = Array.isArray(param) ? param : [param];
  const date = getYearMonthLabel(paramsArr[0]?.axisValue);
  let returnStr = `<div class=${'basic-formatter-box'}>
    <div class=${'basic-formatter-header'}>
    <p class='basic-formatter-one basic-formatter-title'>${date}</p>
    <p class='basic-formatter-two'>${title}</p></div>`;

  for (let i = 0; i < paramsArr.length; i++) {
    const paramValue = paramsArr[i].value;
    let formatted: any = null;
    if (paramValue !== null && paramValue !== undefined) {
      if (isNaN(paramValue)) {
        formatted = '-';
      } else if (formatter) {
        try {
          const formatRule = (
            formatter === 'thousands2Decimal' || formatter === '0,0.00'
          )
            ? 'db_bm00'
            : formatter;
          formatted = dataFormat({ value: Number(paramValue), formatRule });
        } catch (e) {
          formatted = Number(paramValue).toLocaleString();
        }
      } else {
        formatted = Number(paramValue).toLocaleString();
      }
    } else {
      formatted = '-';
    }

    if (unit === 'hour') {
      formatted = `${formatted}H`;
    }
    // Currency symbol lives in chart title (e.g. "ARPU ($)"); tooltip shows value only.
    const radio = paramsArr[i].marker || '';
    returnStr += `<div class=${'basic-formatter-header'}>
      <p class=${'basic-formatter-one'}>${radio}&nbsp;${paramsArr[i].seriesName}</p>
      <p class=${'basic-formatter-two'}>${formatted}</p></div>`;
  }
  returnStr += '</div>';
  return returnStr;
};

interface IChartConfigOptions {
  formatter?: string;
  unit?: string;
  tooltipClassName?: string;
}

export const buildTrendChartConfig = ({
  formatter,
  unit,
  tooltipClassName = 'dbs-live-metric-trend-chart-tooltip',
}: IChartConfigOptions) => (opt: any): any => {
  opt = getCardChart(opt, { timeType: 'day' });
  opt = xAxisHighLight(opt, {
    lineStyle: {
      type: 'solid',
    },
    formatter: (params: any) => getMonthLabel(params?.value),
    symbolSize: 8,
    fontSize: 11,
  });
  opt.grid = {
    x: 6,
    y: 12,
    x2: 6,
    y2: 0,
    containLabel: true,
  };

  const isPercentType = formatter && (
    formatter.includes('percent') ||
    formatter.includes('Percent') ||
    formatter === 'percent_int' ||
    formatter.startsWith('db_percent') ||
    formatter.startsWith('db_abs_percent')
  );

  if (isPercentType) {
    opt.yAxis.axisLabel.formatter = (value: number) => yAxisFormat(value, true, 2, true);
  } else if (unit === 'hour') {
    opt.yAxis.axisLabel.formatter = (value: any) => {
      const formatLabel = value ? unitConversion(value, true) : value;
      return `${formatLabel}H`;
    };
  } else {
    opt.yAxis.axisLabel.formatter = (value: number) => (
      value ? unitConversion(value, true) : value
    );
  }

  opt.xAxis = [{
    ...(opt.xAxis[0] || {}),
    boundaryGap: false,
    axisLabel: {
      ...(opt.xAxis[0]?.axisLabel || {}),
      fontSize: 11,
      color: '#BBBDCB',
      formatter: (value: string) => getMonthLabel(value),
    },
  }];
  opt.yAxis.axisLabel.fontSize = 11;
  opt.yAxis.axisLabel.color = '#BBBDCB';
  opt.tooltip.confine = false;
  opt.tooltip = {
    ...opt.tooltip,
    className: tooltipClassName,
    appendToBody: true,
    axisPointer: {
      ...opt.tooltip?.axisPointer,
      label: {
        ...opt.tooltip?.axisPointer?.label,
        padding: [5, 1, 0, 1],
        backgroundColor: 'transparent',
      },
    },
  };
  return opt;
};

// Match Live Visualization All trend chart palette.
const LIVE_METRIC_TREND_CURRENT_COLOR = '#7F6FF1';
const LIVE_METRIC_TREND_PREVIOUS_YEAR_COLOR = '#74CDF6';

const buildTrendAreaGradient = (color: string) => ({
  opacity: 0.25,
  color: {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color },
      { offset: 1, color: '#FFF' },
    ],
  },
});

export const buildLiveMetricTrendCurrentSeries = (
  name: string,
  data: Array<number | null>,
) => ({
  name,
  type: 'line',
  color: LIVE_METRIC_TREND_CURRENT_COLOR,
  showSymbol: false,
  itemStyle: { color: LIVE_METRIC_TREND_CURRENT_COLOR },
  lineStyle: { type: 'solid', color: LIVE_METRIC_TREND_CURRENT_COLOR },
  areaStyle: buildTrendAreaGradient(LIVE_METRIC_TREND_CURRENT_COLOR),
  data,
});

export const buildLiveMetricTrendPreviousYearSeries = (
  name: string,
  data: Array<number | null>,
) => ({
  name,
  type: 'line',
  color: LIVE_METRIC_TREND_PREVIOUS_YEAR_COLOR,
  showSymbol: true,
  itemStyle: { color: LIVE_METRIC_TREND_PREVIOUS_YEAR_COLOR },
  lineStyle: { type: 'dotted', color: LIVE_METRIC_TREND_PREVIOUS_YEAR_COLOR },
  areaStyle: buildTrendAreaGradient(LIVE_METRIC_TREND_PREVIOUS_YEAR_COLOR),
  data,
});
